import { Component } from "react";
import { isRouteErrorResponse, useNavigate, useRouteError } from "react-router-dom";

function textOf(error) {
  if (!error) return "";
  if (error instanceof Error) return error.message;
  if (typeof error === "object" && error && "message" in error) return String(error.message || "");
  return String(error);
}

function messageFor(error) {
  if (/insertBefore/i.test(textOf(error))) return "The page failed to update. Reload to continue.";
  if (isRouteErrorResponse(error) && error.status === 404) return "Page not found";
  return "Something went wrong.";
}

function RouteErrorView({ error }) {
  const navigate = useNavigate();
  const missing = isRouteErrorResponse(error) && error.status === 404;
  const detail = import.meta.env.DEV && !missing ? textOf(error) : "";
  return (
    <div className="route-error-screen">
      <style>{`
        .route-error-screen {
          min-height: 100dvh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 16px;
          box-sizing: border-box;
          padding: 24px;
          text-align: center;
          background: var(--color-bg,#0c0e12);
          color: #ffffff;
          font-family: inherit;
        }
        .route-error-screen p { margin: 0; max-width: 360px; font-size: 16px; line-height: 1.4; }
        .route-error-actions { display: flex; gap: 12px; }
        .route-error-screen button {
          appearance: none;
          height: 40px;
          padding: 0 18px;
          border-radius: 8px;
          font: inherit;
          font-size: 14px;
          cursor: pointer;
        }
        .route-error-screen button.reload { background: var(--showcase-accent,#3dff9a); color: #04140c; border: 0; }
        .route-error-screen button.back {
          background: transparent;
          color: inherit;
          border: 1px solid color-mix(in srgb, currentColor 35%, transparent);
        }
        .route-error-detail { font-size: 12px; opacity: 0.7; }
      `}</style>
      <p>{messageFor(error)}</p>
      {missing ? null : (
        <div className="route-error-actions">
          <button type="button" className="reload" onClick={() => window.location.reload()}>Reload</button>
          <button type="button" className="back" onClick={() => navigate("/", { replace: true })}>Back home</button>
        </div>
      )}
      {detail ? <p className="route-error-detail">{detail}</p> : null}
    </div>
  );
}

export default function RouteError() {
  const error = useRouteError();
  console.error("Route error:", error);
  return <RouteErrorView error={error} />;
}

export class RouteErrorBoundary extends Component {
  state = { error: null };
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error) {
    console.error("Route error:", error);
  }
  render() {
    if (this.state.error) return <RouteErrorView error={this.state.error} />;
    return this.props.children;
  }
}
